import React from 'react'
import { connect } from 'react-redux'
import { Paper, Typography } from '@material-ui/core'
import { Note as NoteIcon, DragIndicator as DragIcon } from '@material-ui/icons'
import QuickCaptureCard from './QuickCaptureCard'
import { nextCascadePos, atCapacity } from './captureLayout'
import { loadDrafts, saveDrafts } from './captureDrafts'

const newId = () => `qc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

// Label for a minimized card's chip: a short preview of the body, or a fallback.
const chipLabel = (body) => {
  const trimmed = (body || '').trim()
  if (!trimmed) return 'Empty capture'
  return trimmed.length > 20 ? trimmed.slice(0, 20) + '…' : trimmed
}

const DRAG_THRESHOLD = 5

const MinimizedChip = ({ capture, onRestore, onDrag }) => {
  const didDrag = React.useRef(false)

  const onPointerDown = (e) => {
    e.preventDefault()
    didDrag.current = false
    const startX = e.clientX
    const startY = e.clientY
    const origin = { ...capture.pos }

    const onMove = (ev) => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      if (!didDrag.current && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return
      didDrag.current = true
      onDrag({
        x: Math.max(0, origin.x + dx),
        y: Math.max(0, origin.y + dy),
      })
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (!didDrag.current) onRestore(capture.id)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <Paper
      elevation={6}
      onPointerDown={onPointerDown}
      style={{
        position: 'fixed',
        left: capture.pos.x,
        top: capture.pos.y,
        zIndex: 1500,
        padding: '4px 10px',
        cursor: 'grab',
        background: '#fffde7',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: 220,
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <DragIcon fontSize="small" style={{ color: '#bfae4e', flexShrink: 0 }} />
      <NoteIcon fontSize="small" style={{ color: '#bfae4e', flexShrink: 0 }} />
      <Typography variant="caption" style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {chipLabel(capture.body)}
      </Typography>
    </Paper>
  )
}
const QuickCaptureManager = React.forwardRef(({ url, initialScopeLabel, initialScopeProject, onToast }, ref) => {
  const [captures, setCaptures] = React.useState(() => loadDrafts())
  const saveTimer = React.useRef(null)

  // Debounced persistence of the whole array.
  React.useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => saveDrafts(captures), 400)
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current) }
  }, [captures])

  const addCapture = React.useCallback(() => {
    setCaptures(prev => {
      if (atCapacity(prev.length)) {
        if (onToast) onToast('5 captures open — save or close one first.')
        return prev
      }
      // Cascade by the count of currently-open (non-minimized) cards so a new
      // card slots into the visible stack rather than drifting past minimized ones.
      const openCount = prev.filter(c => !c.minimized).length
      return [...prev, {
        id: newId(),
        body: '',
        scopeLabel: initialScopeLabel || '',
        scopeProject: initialScopeProject || null,
        attachments: [],
        pos: nextCascadePos(openCount),
        minimized: false,
      }]
    })
  }, [initialScopeLabel, initialScopeProject, onToast])

  React.useImperativeHandle(ref, () => ({ addCapture }), [addCapture])

  const updateCapture = (id, patch) => {
    setCaptures(prev => prev.map(c => (c.id === id ? { ...c, ...patch } : c)))
  }

  const removeCapture = (id) => {
    setCaptures(prev => prev.filter(c => c.id !== id))
  }

  // Save success removes the card and surfaces the same confirmation toast the
  // single-instance modal showed.
  const handleSaved = (id) => {
    removeCapture(id)
    if (onToast) onToast('Capture saved')
  }

  const minimizeCapture = (id) => updateCapture(id, { minimized: true })
  const restoreCapture = (id) => updateCapture(id, { minimized: false })

  const open = captures.filter(c => !c.minimized)
  const minimized = captures.filter(c => c.minimized)

  return (
    <>
      {open.map(c => (
        <QuickCaptureCard
          key={c.id}
          id={c.id}
          body={c.body}
          scopeLabel={c.scopeLabel}
          scopeProject={c.scopeProject}
          attachments={c.attachments}
          pos={c.pos}
          url={url}
          onChange={updateCapture}
          onSave={handleSaved}
          onClose={removeCapture}
          onMinimize={minimizeCapture}
        />
      ))}

      {/* Minimized chips — each stays at its card's last position and is draggable */}
      {minimized.map(c => (
        <MinimizedChip
          key={c.id}
          capture={c}
          onRestore={restoreCapture}
          onDrag={(newPos) => updateCapture(c.id, { pos: newPos })}
        />
      ))}
    </>
  )
})

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps, null, null, { forwardRef: true })(QuickCaptureManager)
